import { defineElement, FC, onDidLoad, useElement, useListen } from '@atomify/hooks';
import { BPDFormElement, BPDInput, PagesProps } from '@source/components/atoms';
import {
    getSmartSubscriptionProject,
    smartSubscriptionStore,
} from '@source/store/smart-subscription';
import { useButtonLoadingState, usePostcodeChecker } from '@source/utilities/hooks';
import {
    getSubscriptionAddress,
    getSubscriptionUser,
    isStoreAddressDataComplete,
    subscribeToProjectPhase,
    subscriptionStore,
} from '@store/subscription/store';
import { SubscriptionStoreAddress } from '@store/subscription/store.types';

export const SUBSCRIPTION_MODAL_ID = 'smart-subscription-modal';

interface SmartSubscriptionProps {
    bpdPages: PagesProps;
}

const SMART_SUBSCRIPTION_POSTCODE_INPUT_HOOK = '[js-hook-smart-subscription-zipcode] bpd-input';
const SMART_SUBSCRIPTION_HOUSENUMBER_INPUT_HOOK =
    '[js-hook-smart-subscription-housenumber] bpd-input';
const SMART_SUBSCRIPTION_HOUSENUMBER_ADDITION_INPUT_HOOK =
    '[js-hook-smart-subscription-housenumber-suffix] bpd-input';
const SMART_SUBSCRIPTION_STREET_INPUT_HOOK = '[js-hook-smart-subscription-street] bpd-input';
const SMART_SUBSCRIPTION_PLACE_INPUT_HOOK = '[js-hook-smart-subscription-city] bpd-input';
const SMART_SUBSCRIPTION_PHONENUMBER_INPUT_HOOK =
    '[js-hook-smart-subscription-phonenumber] bpd-input';
const SMART_SUBSCRIPTION_COUNTRY_INPUT_HOOK = '[js-hook-smart-subscription-country] bpd-input';
const SMART_SUBSCRIPTION_SUBMIT_CTA_HOOK = '[js-hook-smart-subscription-submit]';

export const SmartSubscription: FC<SmartSubscriptionProps> = ({ element }) => {
    const form = useElement<BPDFormElement>('[js-hook-smart-subscription-project-signup-form]');

    // Element query bindings for dynamic use.
    useElement<PagesProps>('bpd-pages', { as: 'bpdPages' });

    useListen(form, 'serializedForm', async (e: CustomEvent) => handleFormSubmit(e));

    const areaId = window.__ENVIRONMENT_DETAILS__.areaId;

    const zipcodeInput = useElement<BPDInput>(SMART_SUBSCRIPTION_POSTCODE_INPUT_HOOK);
    const houseNumberInput = useElement<BPDInput>(SMART_SUBSCRIPTION_HOUSENUMBER_INPUT_HOOK);
    const houseNumberAdditionInput = useElement<BPDInput>(
        SMART_SUBSCRIPTION_HOUSENUMBER_ADDITION_INPUT_HOOK,
    );
    const streetInput = useElement<BPDInput>(SMART_SUBSCRIPTION_STREET_INPUT_HOOK);
    const cityInput = useElement<BPDInput>(SMART_SUBSCRIPTION_PLACE_INPUT_HOOK);
    const phoneNumberInput = useElement<BPDInput>(SMART_SUBSCRIPTION_PHONENUMBER_INPUT_HOOK);
    const countryInput = useElement<BPDInput>(SMART_SUBSCRIPTION_COUNTRY_INPUT_HOOK);

    const postcodeChecker = usePostcodeChecker({
        element,
        hooks: {
            postcodeHook: SMART_SUBSCRIPTION_POSTCODE_INPUT_HOOK,
            houseNumberHook: SMART_SUBSCRIPTION_HOUSENUMBER_INPUT_HOOK,
            houseNumberAdditionHook: SMART_SUBSCRIPTION_HOUSENUMBER_ADDITION_INPUT_HOOK,
            streetNameHook: SMART_SUBSCRIPTION_STREET_INPUT_HOOK,
            placeNameHook: SMART_SUBSCRIPTION_PLACE_INPUT_HOOK,
        },
    });

    const subTitle = useElement<HTMLElement>('[js-hook-smart-subscription-confirmation-phases]');

    const setSubmitButtonLoading = useButtonLoadingState(SMART_SUBSCRIPTION_SUBMIT_CTA_HOOK);

    let userAddress: SubscriptionStoreAddress | undefined = getSubscriptionAddress();

    onDidLoad(() => {
        postcodeChecker?.initialize();
        if (userAddress) {
            element.bpdPages.setPage(isStoreAddressDataComplete(userAddress) ? '1' : '0');
            preFillModal(userAddress);
        }
    });

    subscriptionStore.subscribe(
        ({ address }) => {
            userAddress = address;

            if (userAddress && isStoreAddressDataComplete(userAddress)) {
                element.bpdPages.setPage('1');
            }
        },
        ['address'],
    );

    smartSubscriptionStore.subscribe(
        ({ project }) => {
            subTitle.current!.innerHTML = `Je bent aangemeld voor de woningtypes van project ${project.name}`;
        },
        ['project'],
    );

    function preFillModal(userAddress: SubscriptionStoreAddress) {
        const { zipcode, houseNumber, houseNumberAddition } = userAddress;
        zipcode && zipcodeInput.current!.rebindValue(zipcode);
        houseNumber && houseNumberInput.current!.rebindValue(houseNumber);
        houseNumberAddition && houseNumberAdditionInput.current!.rebindValue(houseNumberAddition);

        const { street, city, phoneNumber, country } = userAddress;
        street && streetInput.current!.rebindValue(street);
        city && cityInput.current!.rebindValue(city);
        phoneNumber && phoneNumberInput.current!.rebindValue(phoneNumber);
        country && countryInput.current!.rebindValue(country);
    }

    async function handleFormSubmit(e: CustomEvent) {
        const formData = e.detail;

        setSubmitButtonLoading(true);
        try {
            const userInfo = getSubscriptionUser();
            const smartSubscriptionProject = getSmartSubscriptionProject();
            const { projectPhaseId, districtId, area, name } = smartSubscriptionProject;

            if (!(userInfo && projectPhaseId && name)) return;

            await subscribeToProjectPhase({
                areaInfo: {
                    projectPhaseId: projectPhaseId,
                    areaProjectId: areaId,
                    areaProjectName: area || '',
                    districtId: districtId || '',
                },
                userInfo: {
                    firstName: userInfo.firstName || '',
                    lastName: userInfo.lastName || '',
                    initials: userInfo.initials,
                    email: userInfo.email || '',
                    gender: userInfo.gender,
                    website: formData.website,
                },
                address: {
                    zipcode: formData.postalCode,
                    street: formData.street,
                    houseNumber: formData.houseNumber,
                    houseNumberAddition: formData.houseNumberSuffix,
                    city: formData.city,
                    phoneNumber: formData.phoneNumber,
                    country: formData.country,
                },
                GTMEventLabel: area || '',
            });

            element.bpdPages.selectNext();
        } finally {
            setSubmitButtonLoading(false);
        }
    }
};

defineElement('smart-subscription', SmartSubscription);
